import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAuth } from "oidc-react";

const TEAM_MESSAGES_STORAGE_KEY = "teamMessages.readIds.v1";
const TEAM_MESSAGES_ACK_IMMEDIATE_STORAGE_KEY =
  "teamMessages.acknowledgedImmediateIds.v1";
const TEAM_MESSAGES_STALE_MS = 60 * 1000;
const DEFAULT_POLL_INTERVAL_MS = 5 * 60 * 1000;

// Polling interval is configurable via VITE_TEAM_MESSAGES_POLL_INTERVAL_MS (in ms).
// Falls back to 5 minutes if unset or invalid.
const resolvePollIntervalMs = () => {
  const raw = import.meta.env.VITE_TEAM_MESSAGES_POLL_INTERVAL_MS;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0
    ? parsed
    : DEFAULT_POLL_INTERVAL_MS;
};

const readPersistedReadTeamMessageIds = () => {
  try {
    const raw = localStorage.getItem(TEAM_MESSAGES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error("Failed to read team messages read state", e);
    return [];
  }
};

const writePersistedReadTeamMessageIds = (ids) => {
  try {
    localStorage.setItem(TEAM_MESSAGES_STORAGE_KEY, JSON.stringify(ids));
  } catch (e) {
    console.error("Failed to persist team messages read state", e);
  }
};

const readPersistedAcknowledgedImmediateIds = () => {
  try {
    const raw = localStorage.getItem(TEAM_MESSAGES_ACK_IMMEDIATE_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error("Failed to read acknowledged immediate messages state", e);
    return [];
  }
};

const writePersistedAcknowledgedImmediateIds = (ids) => {
  try {
    localStorage.setItem(
      TEAM_MESSAGES_ACK_IMMEDIATE_STORAGE_KEY,
      JSON.stringify(ids)
    );
  } catch (e) {
    console.error("Failed to persist acknowledged immediate messages state", e);
  }
};

const EMPTY_CONTEXT_VALUE = {
  teamMessages: [],
  readTeamMessageIds: new Set(),
  unreadCount: 0,
  immediateMessages: [],
  acknowledgeImmediate: () => {},
  markAllRead: () => {},
  fetchIfStale: () => {},
  refetch: () => {},
};

const TeamMessagesContext = createContext(EMPTY_CONTEXT_VALUE);

export const useTeamMessages = () => useContext(TeamMessagesContext);

export const TeamMessagesProvider = ({ children }) => {
  const rootUrl = import.meta.env.VITE_BACKEND_API_ROOT_URL;
  const pollIntervalMs = resolvePollIntervalMs();

  const auth = useAuth();
  const accessToken = auth?.userData?.access_token;
  const hasToken = Boolean(accessToken);

  // Held in a ref so the polling effect doesn't restart on every OIDC silent renewal.
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;

  const [teamMessages, setTeamMessages] = useState([]);
  const [readTeamMessageIds, setReadTeamMessageIds] = useState(
    () => new Set(readPersistedReadTeamMessageIds())
  );
  const [acknowledgedImmediateIds, setAcknowledgedImmediateIds] = useState(
    () => new Set(readPersistedAcknowledgedImmediateIds())
  );
  const [lastFetchAt, setLastFetchAt] = useState(0);

  const fetchTeamMessages = useCallback(async () => {
    const token = accessTokenRef.current;
    if (!token) return;
    try {
      const response = await fetch(`${rootUrl}user/teamMessages`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        const sorted = Array.isArray(data)
          ? [...data].sort(
              (a, b) =>
                new Date(b.created).getTime() - new Date(a.created).getTime()
            )
          : [];
        setTeamMessages(sorted);
        setLastFetchAt(Date.now());
      }
    } catch (error) {
      console.error("Error fetching team messages:", error);
    }
  }, [rootUrl]);

  // Initial fetch + recurring poll. The interval is configurable via
  // VITE_TEAM_MESSAGES_POLL_INTERVAL_MS (default 5 minutes).
  useEffect(() => {
    if (!hasToken) return undefined;
    fetchTeamMessages();
    const id = setInterval(fetchTeamMessages, pollIntervalMs);
    return () => clearInterval(id);
  }, [hasToken, fetchTeamMessages, pollIntervalMs]);

  const markAllRead = useCallback(() => {
    if (teamMessages.length === 0) return;
    // Replace the read set with current message IDs so localStorage doesn't grow
    // unbounded as messages expire on the server.
    const next = new Set(teamMessages.map((m) => m.id));
    setReadTeamMessageIds(next);
    writePersistedReadTeamMessageIds([...next]);
  }, [teamMessages]);

  const fetchIfStale = useCallback(() => {
    if (Date.now() - lastFetchAt > TEAM_MESSAGES_STALE_MS) {
      fetchTeamMessages();
    }
  }, [lastFetchAt, fetchTeamMessages]);

  // Mark a "show immediately" message as acknowledged so its blocking dialog is
  // not shown again. Also mark it read so the notification badge reflects it.
  const acknowledgeImmediate = useCallback(
    (id) => {
      const currentIds = new Set(teamMessages.map((m) => m.id));
      setAcknowledgedImmediateIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        // Prune to current messages so localStorage doesn't grow unbounded as
        // messages expire on the server.
        const pruned = new Set([...next].filter((x) => currentIds.has(x)));
        writePersistedAcknowledgedImmediateIds([...pruned]);
        return pruned;
      });
      setReadTeamMessageIds((prev) => {
        const next = new Set(prev);
        next.add(id);
        writePersistedReadTeamMessageIds([...next]);
        return next;
      });
    },
    [teamMessages]
  );

  const unreadCount = teamMessages.reduce(
    (acc, m) => (readTeamMessageIds.has(m.id) ? acc : acc + 1),
    0
  );

  // Messages flagged "show immediately" in ECO that the technician has not yet
  // acknowledged. Shown in a blocking dialog rather than only as a badge.
  const immediateMessages = teamMessages.filter(
    (m) => m.showImmediately && !acknowledgedImmediateIds.has(m.id)
  );

  const value = {
    teamMessages,
    readTeamMessageIds,
    unreadCount,
    immediateMessages,
    acknowledgeImmediate,
    markAllRead,
    fetchIfStale,
    refetch: fetchTeamMessages,
  };

  return (
    <TeamMessagesContext.Provider value={value}>
      {children}
    </TeamMessagesContext.Provider>
  );
};
